import { useEffect, useRef, useState } from 'react'
import { useTexte } from '../texte'
import {
  HOECHSTENS_MS,
  starteAufnahme,
  type Aufnahme,
  type LaufendeAufnahme,
} from '../lib/sprachaufnahme'

/**
 * The microphone: start, pause, resume, the clock and the ceiling.
 *
 * Pulled out of `Composer.tsx` in 1.23.0. It ends where the recording does:
 * the finished blob goes to `onFertig`, and what becomes of it -- a tile,
 * the conversion, the upload -- is the business of `useAttachments`, which
 * does the same for a photo and a video. Two ways into the same list would
 * be two truths.
 *
 * **Two things travel through refs**, and for the same reason: the timer is
 * created when recording starts and would otherwise hold the state of that
 * moment. The ceiling arrives with the settings, possibly later; the
 * caller's callback changes with every keystroke in the line above.
 */
export function useRecording({
  deckeSekunden,
  onFertig,
}: {
  /** Die Decke in Sekunden -- aus den Einstellungen, darf noch fehlen. */
  deckeSekunden: number | undefined
  /** Die fertige Aufnahme, und ob sie gleich hinaus soll. */
  onFertig: (roh: Aufnahme, undSenden: boolean) => void
}) {
  const t = useTexte()
  // Die laufende Aufnahme -- als Ref, weil sie kein Neuzeichnen auslöst, und
  // die verstrichene Zeit daneben als Zustand, weil sie eine.
  const aufnahme = useRef<LaufendeAufnahme | null>(null)
  const [nimmtAuf, setNimmtAuf] = useState(false)
  const [pausiert, setPausiert] = useState(false)
  const [seit, setSeit] = useState(0)
  /*
   * **Der `AudioContext` entsteht im Tipp und lebt danach weiter.**
   * Safari lässt ihn nur in einer Benutzergeste anlaufen, und nach dem
   * `await` auf `getUserMedia` ist die Geste vorbei. Einmal angelaufen, darf
   * er bleiben: ein zweiter Tipp findet ihn vor, und ein geschlossener wäre
   * beim zweiten Mal wieder ein Fall für die Geste.
   */
  const tonkontext = useRef<AudioContext | null>(null)
  const [tonHinweis, setTonHinweis] = useState<string | null>(null)

  const deckeRef = useRef(deckeSekunden)
  deckeRef.current = deckeSekunden
  const fertigRef = useRef(onFertig)
  fertigRef.current = onFertig

  /**
   * Aufnehmen anfangen — und den Zähler mitlaufen lassen.
   *
   * **Keine stille Absage:** verweigert das Gerät das Mikrofon, steht der
   * Grund als Text da. Ein Knopf, auf den nichts folgt, ist das Schlimmste.
   */
  async function nimmAuf() {
    setTonHinweis(null)
    /*
     * **Noch in der Geste, vor jedem `await`.** Danach ist der Tipp vorbei,
     * und Safari lässt einen `AudioContext` dann nicht mehr anlaufen — die
     * Wellenform bliebe flach. Scheitert er, läuft alles Übrige weiter.
     */
    if (tonkontext.current === null) {
      try {
        tonkontext.current = new AudioContext()
      } catch {
        tonkontext.current = null
      }
    }
    void tonkontext.current?.resume()

    try {
      aufnahme.current = await starteAufnahme(tonkontext.current ?? undefined)
      setNimmtAuf(true)
      setPausiert(false)
      setSeit(0)
    } catch {
      setTonHinweis(
        t.chat.mikrofonGesperrt,
      )
    }
  }

  /** Anhalten und weitermachen — der Zähler steht dabei still. */
  function pausiereAufnahme() {
    aufnahme.current?.pausieren()
    setPausiert(true)
  }

  function weiterAufnehmen() {
    aufnahme.current?.weiter()
    setPausiert(false)
  }

  /**
   * Aufnahme beenden und als Anhang aufnehmen — wie ein fertiges Bild.
   *
   * **`undSenden` ist der Kern der Meldung** (#63): „Zum Beenden + Senden dann
   * direkt den Senden-Knopf drücken." Gesendet wird nicht hier, sondern über
   * die Vormerkung, die es für Fotos längst gibt — die Aufnahme muss erst
   * hochgeladen sein, und genau darauf wartet `sendeWennFertig`. Zwei Wege
   * zum Senden wären zwei Wahrheiten.
   *
   * **Hier endet das Mikrofon.** Was aus dem Blob wird, entscheidet der
   * Aufrufer: die Kachel, das Umwandeln und der Upload stehen bei den
   * Anhängen, wo sie für Foto und Video auch stehen.
   */
  async function beende(undSenden = false) {
    const laufend = aufnahme.current
    if (!laufend) return
    aufnahme.current = null
    setNimmtAuf(false)
    setPausiert(false)

    const roh = await laufend.stoppen()
    if (roh.blob.size === 0) return
    // **Über einen Ref und nicht über die Abhängigkeiten.** Denselben Weg
    // nimmt die Decke unten, und zwei Wege zu einem Anhang wären zwei
    // Wahrheiten.
    fertigRef.current(roh, undSenden)
  }

  function brichAufnahmeAb() {
    aufnahme.current?.abbrechen()
    aufnahme.current = null
    setNimmtAuf(false)
    setPausiert(false)
  }

  /*
   * Der Zähler, und die Decke: bei zwei Minuten ist Schluss, von selbst.
   *
   * **Gefragt statt gezählt.** Hier wurden 250 Millisekunden je Schlag
   * addiert; mit einer Pause zählte das weiter, und am Ende stünde an der
   * Sprechblase eine Länge, die das Abspielen nie erreicht. Die Aufnahme
   * selbst weiß, wie lange sie schon läuft (`dauerJetzt()`), und sie zählt
   * die Pausen nicht mit. Ein Zeitgeber, der zu spät kommt, verschiebt die
   * Zahl damit auch nicht mehr.
   *
   * **Und was von selbst endet, geht nicht von selbst hinaus.** Die Decke
   * beendet die Aufnahme und legt sie als Anhang hin — gesendet wird sie erst
   * auf Druck. Etwas ungefragt zu verschicken, wäre das Gegenteil dessen, was
   * der Knopf verspricht.
   */
  useEffect(() => {
    if (!nimmtAuf) return
    const uhr = setInterval(() => {
      const jetzt = aufnahme.current?.dauerJetzt() ?? 0
      setSeit(jetzt)
      // The ceiling is a setting since 1.9.0; the constant only bridges the
      // moment before the settings arrive.
      //
      // **Aus dem Ref und nicht aus der Closure** (1.23.0): der Zeitgeber
      // entsteht, wenn die Aufnahme anläuft, und behielte sonst den Stand von
      // damals -- eine Einstellung, die einen Wimpernschlag später eintrifft,
      // bliebe wirkungslos.
      const decke = (deckeRef.current ?? HOECHSTENS_MS / 1000) * 1000
      if (jetzt >= decke) void beende(false)
    }, 100)
    return () => clearInterval(uhr)
    // `beende` hängt an nichts, was sich währenddessen ändert.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nimmtAuf])

  return {
    nimmtAuf,
    pausiert,
    seit,
    hinweis: tonHinweis,
    sagHinweis: setTonHinweis,
    /** Beide lesen die laufende Aufnahme -- sie steht, solange gezeichnet wird. */
    kannPausieren: aufnahme.current?.kannPausieren ?? false,
    pegel: () => aufnahme.current?.pegel() ?? 0,
    starte: nimmAuf,
    pausiere: pausiereAufnahme,
    weiter: weiterAufnehmen,
    beende,
    abbrich: brichAufnahmeAb,
  }
}
